import _ from 'lodash';
import { updateAsset as apiUpdateAsset } from '../api/vault/assets';
import { VAULT_MEDIA_TYPE, VAULT_VIDEO_TYPE } from '../constants';
import toast from '../utils/toast';
import { queryGamesAction, updateSearchHit } from './search';
import { updateRawAssetDetails as updateNonEditableRawAssetDetails } from './rawAssetDetails';
import { generateRenderableAssetDetails, generateEditableAssetDetails } from '../utils/assetDetails/generateRenderableAssetDetails';
import { prepareAssetUpdates } from '../utils/assetDetails/prepareAssetUpdates';
import { addAssetDetails, addEditableAssetDetails } from './previewAsset';
import { selectUserRole } from './user/userOrgRole';

const ENABLE_EDIT_METADATA = 'ENABLE_EDIT_METADATA';
const DISABLE_EDIT_METADATA = 'DISABLE_EDIT_METADATA';
const UPDATE_RAW_ASSET_DETAILS = 'UPDATE_RAW_ASSET_DETAILS';
const RESET_RAW_ASSET_DETAILS = 'RESET_RAW_ASSET_DETAILS';
const UPDATE_ASSET_UPDATES = 'UPDATE_ASSET_UPDATES';
const RESET_ASSET_UPDATES = 'RESET_ASSET_UPDATES';
const UPDATE_IS_UPDATING = 'UPDATE_IS_UPDATING';

const INITIAL_STATE = {
  isEditable: false,
  assetDetailsCopy: [],
  editableAssetDetailsCopy: [],
  rawAssetDetails: {},
  updates: {},
  isUpdating: false,
};

export default function reducer(state = INITIAL_STATE, action) {
  switch (action.type) {
    case ENABLE_EDIT_METADATA:
      return {
        ...state,
        updates: {},
        isEditable: true,
        assetDetailsCopy: action.payload.assetDetails,
        editableAssetDetailsCopy: action.payload.editableAssetDetails,
      };
    case DISABLE_EDIT_METADATA:
      return {
        ...state,
        isEditable: false,
        assetDetailsCopy: [],
        editableAssetDetailsCopy: [],
      };
    case UPDATE_RAW_ASSET_DETAILS:
      return {
        ...state,
        rawAssetDetails: action.payload,
      };
    case RESET_RAW_ASSET_DETAILS:
      return {
        ...state,
        rawAssetDetails: {},
      };
    case UPDATE_ASSET_UPDATES:
      return {
        ...state,
        updates: {
          ...state.updates,
          ...action.payload,
        },
      };
    case RESET_ASSET_UPDATES:
      return {
        ...state,
        updates: {},
      };
    case UPDATE_IS_UPDATING:
      return {
        ...state,
        isUpdating: action.payload,
      };
    default:
      return state;
  }
}

export function enableEditMetadata(assetDetails, editableAssetDetails) {
  return {
    type: ENABLE_EDIT_METADATA,
    payload: { assetDetails, editableAssetDetails },
  };
}

export function disableEditMetadata() {
  return {
    type: DISABLE_EDIT_METADATA,
  };
}

export function updateRawAssetDetails(rawAssetDetails) {
  return {
    type: UPDATE_RAW_ASSET_DETAILS,
    payload: rawAssetDetails,
  };
}

export function resetRawAssetDetails() {
  return {
    type: RESET_RAW_ASSET_DETAILS,
  };
}

export const resetAssetUpdates = () => ({
  type: RESET_ASSET_UPDATES,
});

export const addAssetUpdates = (update) => ({
  type: UPDATE_ASSET_UPDATES,
  payload: update,
});

export const updateIsUpdating = (isEditing) => ({
  type: UPDATE_IS_UPDATING,
  payload: isEditing,
});

export const exitEditMode = () => (dispatch, getState) => {
  dispatch(updateIsUpdating(false));
  dispatch(disableEditMetadata());
  dispatch(resetRawAssetDetails());
};

export const updateAsset = (assetId) => async (dispatch, getState) => {
  dispatch(updateIsUpdating(true));
  const state = getState();
  const { editMetadata: { updates, rawAssetDetails }, metatags, securityProfiles } = state;
  const userRole = selectUserRole(state);
  if (!updates || _.isEmpty(updates)) {
    dispatch(updateIsUpdating(false));
    exitEditMode()(dispatch, getState);
    return;
  }

  const { updates: preparedUpdates, error } = prepareAssetUpdates(updates, {
    displayName: userRole.userInfo.displayName,
    gameDateMode: 'dropWhenEmpty',
  });
  if (error) {
    // Leave the panel in edit mode so the field can be corrected.
    toast(error, { type: toast.TYPE.ERROR });
    dispatch(updateIsUpdating(false));
    return;
  }

  const res = await apiUpdateAsset(assetId, preparedUpdates);
  if (res.status === 200 && res?.data?.data) {
    const updatedAsset = res.data.data;

    const metaTags = metatags?.metaTags;
    const securityProfileIdValueMapping = securityProfiles?.idValueMapping;
    const assetDetails = generateRenderableAssetDetails(
      metaTags,
      rawAssetDetails,
      securityProfileIdValueMapping,
    );
    const editableAssetDetails = generateEditableAssetDetails(
      metaTags,
      rawAssetDetails,
      securityProfileIdValueMapping,
    );
    dispatch(addAssetDetails(assetDetails));
    dispatch(addEditableAssetDetails(editableAssetDetails));

    // va-1229 + va-1083
    // in the event the video is an exchange asset and the title is changed =>
    //   - reload search results
    //   - reset preview panel
    if (rawAssetDetails.type === VAULT_MEDIA_TYPE.video.toString() && rawAssetDetails.videoType === VAULT_VIDEO_TYPE.Exchange.toString()) {
      // initial update necessary to not trigger cancel edit on query/exit
      dispatch(updateNonEditableRawAssetDetails(updatedAsset)); // Maybe
      queryGamesAction(500, async () => {
        dispatch(updateRawAssetDetails(updatedAsset)); // Maybe
      });
    } else {
      dispatch(updateRawAssetDetails(updatedAsset));

      dispatch(updateNonEditableRawAssetDetails(updatedAsset));
      dispatch(updateSearchHit({
        _id: assetId,
        ...updatedAsset,
      }));
    }
    exitEditMode()(dispatch, getState);
  }
  dispatch(updateIsUpdating(false));
};
